import { BASE_CAPACITY, formatWeight, getCapacityBonus, getWeight } from '../../../game/demo/inventory/capacity';
import { items, ItemId } from '../../../game/demo/inventory/items';
import eventBus from '../../utils/emitter-event-bus';
import { InventoryEventBus, InventoryReader } from './inventory.model';

export class InventoryUI {
  private readonly panel = document.querySelector<HTMLElement>('.js-inventory');
  private readonly list = document.querySelector<HTMLElement>('.js-inventory-items');
  // The carry/weight HUD badge doubles as the inventory open/close toggle.
  private readonly toggle = document.querySelector<HTMLElement>('.js-carry-hud');
  private readonly close = document.querySelector<HTMLButtonElement>('.js-inventory-close');

  // The item whose description is currently shown (clicking a row toggles it).
  private selectedId: ItemId | null = null;

  constructor(private readonly reader: InventoryReader) {
    this.toggle?.addEventListener('click', () => this.togglePanel());
    this.close?.addEventListener('click', () => this.hidePanel());
    this.list?.addEventListener('click', (event) => this.onRowClick(event));
    eventBus.on(InventoryEventBus.changed, () => this.render());
    this.render();
  }

  private togglePanel(): void {
    if (!this.panel) return;
    this.panel.hidden = !this.panel.hidden;
  }

  private hidePanel(): void {
    if (this.panel) this.panel.hidden = true;
  }

  private onRowClick(event: MouseEvent): void {
    const row = (event.target as HTMLElement).closest<HTMLElement>('.inventory__item');
    const id = row?.dataset.itemId as ItemId | undefined;
    if (!id) return;
    this.selectedId = id;
    this.render();
  }

  private render(): void {
    if (!this.list) return;
    const state = this.reader.getState();
    const rows = (Object.keys(items) as ItemId[])
      .map((id) => ({ id, name: items[id].name, quantity: state.items[id] ?? 0 }))
      .filter((row) => row.quantity > 0 && !items[row.id].hideInInventory);

    // Default to the first item (and recover if the selected one is gone) so a
    // description is always shown — which makes it clear items can be inspected.
    if (!rows.some((row) => row.id === this.selectedId)) {
      this.selectedId = rows[0]?.id ?? null;
    }

    this.list.innerHTML = '';

    const bonus = getCapacityBonus(state);
    const load = document.createElement('div');
    load.className = 'inventory__load';
    load.appendChild(document.createTextNode(`Load: ${formatWeight(getWeight(state))}/${BASE_CAPACITY + bonus}`));
    if (bonus > 0) {
      const bonusEl = document.createElement('span');
      bonusEl.className = 'inventory__load-bonus';
      bonusEl.textContent = ` (🎒 +${bonus})`;
      bonusEl.title = 'from backpack';
      load.appendChild(bonusEl);
    }
    this.list.appendChild(load);

    if (rows.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'inventory__empty';
      empty.textContent = 'Empty';
      this.list.appendChild(empty);
      return;
    }

    const table = document.createElement('table');
    table.className = 'inventory__table';

    const head = document.createElement('tr');
    const cols: Array<[string, string, string?]> = [
      ['📦', 'inventory__col-item', 'Item'],
      ['⚖', 'inventory__col-weight', 'Weight'],
      ['#', 'inventory__col-qty', 'Quantity'],
    ];
    for (const [text, cls, title] of cols) {
      const th = document.createElement('th');
      th.className = cls;
      th.textContent = text;
      if (title) th.title = title;
      head.appendChild(th);
    }
    const thead = document.createElement('thead');
    thead.appendChild(head);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const row of rows) {
      const tr = document.createElement('tr');
      tr.className = 'inventory__item';
      tr.setAttribute('data-item-id', row.id);
      if (row.id === this.selectedId) tr.classList.add('is-selected');

      const name = document.createElement('td');
      name.className = 'inventory__item-label';
      name.textContent = row.name;

      const qty = document.createElement('td');
      qty.className = 'inventory__item-qty';
      qty.textContent = String(row.quantity);

      const weight = document.createElement('td');
      weight.className = 'inventory__item-weight';
      const totalWeight = items[row.id].weight * row.quantity;
      // Weightless goods (e.g. the rod) show nothing rather than a bare "0".
      weight.textContent = totalWeight > 0 ? formatWeight(totalWeight) : '';

      tr.append(name, weight, qty);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    this.list.appendChild(table);

    if (this.selectedId) {
      const detail = document.createElement('div');
      detail.className = 'inventory__detail js-inventory-detail';

      const detailName = document.createElement('div');
      detailName.className = 'inventory__detail-name';
      detailName.textContent = items[this.selectedId].name;

      const detailText = document.createElement('p');
      detailText.className = 'inventory__detail-text';
      detailText.textContent = items[this.selectedId].description;

      detail.append(detailName, detailText);
      this.list.appendChild(detail);
    }
  }
}
